import type { Note } from './battle-loop';

// Academy BGM — 60 s, C major, peaceful, ~100 bpm, piano-feel (sine/triangle)
// Quarter note at 100 bpm = 0.60 s, half = 1.20 s, whole = 2.40 s
// 4/4 → bar = 2.40 s.  60 s = 25 bars.
// Chord progression (repeated every 8 bars = 19.2 s):
//   Bars 1-2: C major   (C4 E4 G4)
//   Bars 3-4: A minor   (A3 C4 E4)
//   Bars 5-6: F major   (F3 A3 C4)
//   Bars 7-8: G major   (G3 B3 D4)
//   then repeat

// C major scale frequencies
const C4  = 261.63;
const E4  = 329.63;
const F4  = 349.23;
const G4  = 392.00;
const A4  = 440.00;
const B4  = 493.88;
const C5  = 523.25;
const D5  = 587.33;
const E5  = 659.25;
const F5  = 698.46;
const G5  = 783.99;
const A5  = 880.00;

// Lower octave (bass / pad)
const A3  = 220.00;
const C3  = 130.81;
const F3  = 174.61;
const G3  = 196.00;

// ── Helper: build one 8-bar progression phrase starting at `offset` ──────────
// melody: gentle stepwise motion in upper C major range
// bass pad: root + fifth, long sustained half notes, low gain
function phrase(offset: number): Note[] {
  const Q = 0.60;  // quarter note
  const H = 1.20;  // half note
  const W = 2.40;  // whole note

  // Bar positions within phrase (2.40 s per bar)
  const B = (bar: number) => offset + (bar - 1) * W;

  const melody: Note[] = [
    // Bar 1 — C major feel — rise from C4 to E4 to G4
    { freq: C4, startSec: B(1) + 0.00, durationSec: Q,   gain: 0.22, wave: 'sine' },
    { freq: E4, startSec: B(1) + 0.60, durationSec: Q,   gain: 0.22, wave: 'sine' },
    { freq: G4, startSec: B(1) + 1.20, durationSec: H,   gain: 0.25, wave: 'sine' },

    // Bar 2 — C major — high notes, lyrical
    { freq: C5, startSec: B(2) + 0.00, durationSec: H,   gain: 0.25, wave: 'sine' },
    { freq: B4, startSec: B(2) + 1.20, durationSec: H,   gain: 0.22, wave: 'sine' },

    // Bar 3 — A minor — descend A4 → E4
    { freq: A4, startSec: B(3) + 0.00, durationSec: Q,   gain: 0.23, wave: 'sine' },
    { freq: G4, startSec: B(3) + 0.60, durationSec: Q,   gain: 0.22, wave: 'sine' },
    { freq: E4, startSec: B(3) + 1.20, durationSec: H,   gain: 0.25, wave: 'sine' },

    // Bar 4 — A minor — A4 → C5 lilt
    { freq: A4, startSec: B(4) + 0.00, durationSec: H,   gain: 0.23, wave: 'sine' },
    { freq: C5, startSec: B(4) + 1.20, durationSec: H,   gain: 0.25, wave: 'sine' },

    // Bar 5 — F major — F4 A4 C5
    { freq: F4, startSec: B(5) + 0.00, durationSec: Q,   gain: 0.22, wave: 'sine' },
    { freq: A4, startSec: B(5) + 0.60, durationSec: Q,   gain: 0.23, wave: 'sine' },
    { freq: C5, startSec: B(5) + 1.20, durationSec: H,   gain: 0.25, wave: 'sine' },

    // Bar 6 — F major — rise to E5
    { freq: D5, startSec: B(6) + 0.00, durationSec: Q,   gain: 0.22, wave: 'sine' },
    { freq: E5, startSec: B(6) + 0.60, durationSec: Q,   gain: 0.25, wave: 'sine' },
    { freq: D5, startSec: B(6) + 1.20, durationSec: H,   gain: 0.23, wave: 'sine' },

    // Bar 7 — G major — G4 B4 D5
    { freq: G4, startSec: B(7) + 0.00, durationSec: Q,   gain: 0.22, wave: 'sine' },
    { freq: B4, startSec: B(7) + 0.60, durationSec: Q,   gain: 0.23, wave: 'sine' },
    { freq: D5, startSec: B(7) + 1.20, durationSec: H,   gain: 0.25, wave: 'sine' },

    // Bar 8 — G major — resolve down G4
    { freq: B4, startSec: B(8) + 0.00, durationSec: H,   gain: 0.22, wave: 'sine' },
    { freq: G4, startSec: B(8) + 1.20, durationSec: H,   gain: 0.25, wave: 'sine' },
  ];

  const bass: Note[] = [
    // Root bass pad per 2-bar block, very low gain, triangle
    { freq: C3, startSec: B(1), durationSec: W,  gain: 0.10, wave: 'triangle' },
    { freq: C3, startSec: B(2), durationSec: W,  gain: 0.10, wave: 'triangle' },
    { freq: A3, startSec: B(3), durationSec: W,  gain: 0.10, wave: 'triangle' },
    { freq: A3, startSec: B(4), durationSec: W,  gain: 0.10, wave: 'triangle' },
    { freq: F3, startSec: B(5), durationSec: W,  gain: 0.10, wave: 'triangle' },
    { freq: F3, startSec: B(6), durationSec: W,  gain: 0.10, wave: 'triangle' },
    { freq: G3, startSec: B(7), durationSec: W,  gain: 0.10, wave: 'triangle' },
    { freq: G3, startSec: B(8), durationSec: W,  gain: 0.10, wave: 'triangle' },
  ];

  return [...melody, ...bass];
}

// ── Variation B phrase (bars 9-16 within a 25-bar loop): add upper arpeggio ─
function phraseVariant(offset: number): Note[] {
  const Q = 0.60;
  const H = 1.20;
  const W = 2.40;
  const B = (bar: number) => offset + (bar - 1) * W;

  const melody: Note[] = [
    // Bar 1 — C major — arpeggio C5 E5 G5
    { freq: C5, startSec: B(1) + 0.00, durationSec: Q,   gain: 0.22, wave: 'sine' },
    { freq: E5, startSec: B(1) + 0.60, durationSec: Q,   gain: 0.23, wave: 'sine' },
    { freq: G5, startSec: B(1) + 1.20, durationSec: H,   gain: 0.25, wave: 'sine' },

    // Bar 2 — C major — sustained G5 → E5
    { freq: G5, startSec: B(2) + 0.00, durationSec: H,   gain: 0.25, wave: 'sine' },
    { freq: E5, startSec: B(2) + 1.20, durationSec: H,   gain: 0.22, wave: 'sine' },

    // Bar 3 — A minor — A5 → E5
    { freq: A5, startSec: B(3) + 0.00, durationSec: Q,   gain: 0.22, wave: 'sine' },
    { freq: G5, startSec: B(3) + 0.60, durationSec: Q,   gain: 0.22, wave: 'sine' },
    { freq: E5, startSec: B(3) + 1.20, durationSec: H,   gain: 0.25, wave: 'sine' },

    // Bar 4 — A minor — C5 long
    { freq: C5, startSec: B(4) + 0.00, durationSec: W,   gain: 0.25, wave: 'sine' },

    // Bar 5 — F major — F5 A5 C5
    { freq: F5, startSec: B(5) + 0.00, durationSec: Q,   gain: 0.22, wave: 'sine' },
    { freq: A5, startSec: B(5) + 0.60, durationSec: Q,   gain: 0.23, wave: 'sine' },
    { freq: F5, startSec: B(5) + 1.20, durationSec: H,   gain: 0.22, wave: 'sine' },

    // Bar 6 — F major — E5 D5
    { freq: E5, startSec: B(6) + 0.00, durationSec: H,   gain: 0.23, wave: 'sine' },
    { freq: D5, startSec: B(6) + 1.20, durationSec: H,   gain: 0.22, wave: 'sine' },

    // Bar 7 — G major — D5 B4 G4
    { freq: D5, startSec: B(7) + 0.00, durationSec: Q,   gain: 0.22, wave: 'sine' },
    { freq: B4, startSec: B(7) + 0.60, durationSec: Q,   gain: 0.23, wave: 'sine' },
    { freq: G4, startSec: B(7) + 1.20, durationSec: H,   gain: 0.25, wave: 'sine' },

    // Bar 8 — G major — resolve C5
    { freq: C5, startSec: B(8) + 0.00, durationSec: H,   gain: 0.25, wave: 'sine' },
    { freq: B4, startSec: B(8) + 1.20, durationSec: H,   gain: 0.22, wave: 'sine' },
  ];

  const bass: Note[] = [
    { freq: C3, startSec: B(1), durationSec: W,  gain: 0.10, wave: 'triangle' },
    { freq: C3, startSec: B(2), durationSec: W,  gain: 0.10, wave: 'triangle' },
    { freq: A3, startSec: B(3), durationSec: W,  gain: 0.10, wave: 'triangle' },
    { freq: A3, startSec: B(4), durationSec: W,  gain: 0.10, wave: 'triangle' },
    { freq: F3, startSec: B(5), durationSec: W,  gain: 0.10, wave: 'triangle' },
    { freq: F3, startSec: B(6), durationSec: W,  gain: 0.10, wave: 'triangle' },
    { freq: G3, startSec: B(7), durationSec: W,  gain: 0.10, wave: 'triangle' },
    { freq: G3, startSec: B(8), durationSec: W,  gain: 0.10, wave: 'triangle' },
  ];

  return [...melody, ...bass];
}

// ── Coda phrase (bars 17-25 = 9 bars = last 21.6 s of loop) ─────────────────
// A gentle outro: single sustained notes, whole notes, very soft
function coda(offset: number): Note[] {
  const H = 1.20;
  const W = 2.40;
  const B = (bar: number) => offset + (bar - 1) * W;

  return [
    // Bar 1-2 C major
    { freq: E5, startSec: B(1) + 0.00, durationSec: W,  gain: 0.22, wave: 'sine' },
    { freq: C5, startSec: B(2) + 0.00, durationSec: W,  gain: 0.22, wave: 'sine' },
    // Bar 3-4 A minor
    { freq: A4, startSec: B(3) + 0.00, durationSec: W,  gain: 0.23, wave: 'sine' },
    { freq: E5, startSec: B(4) + 0.00, durationSec: W,  gain: 0.22, wave: 'sine' },
    // Bar 5-6 F major
    { freq: F4, startSec: B(5) + 0.00, durationSec: W,  gain: 0.22, wave: 'sine' },
    { freq: A4, startSec: B(6) + 0.00, durationSec: W,  gain: 0.22, wave: 'sine' },
    // Bar 7-8 G major
    { freq: G4, startSec: B(7) + 0.00, durationSec: W,  gain: 0.22, wave: 'sine' },
    { freq: D5, startSec: B(8) + 0.00, durationSec: W,  gain: 0.22, wave: 'sine' },
    // Bar 9 — final whole C resolve
    { freq: C5, startSec: B(9) + 0.00, durationSec: H,  gain: 0.20, wave: 'sine' },
    { freq: G4, startSec: B(9) + 1.20, durationSec: H,  gain: 0.20, wave: 'sine' },

    // Bass pad for coda
    { freq: C3, startSec: B(1), durationSec: W,  gain: 0.09, wave: 'triangle' },
    { freq: C3, startSec: B(2), durationSec: W,  gain: 0.09, wave: 'triangle' },
    { freq: A3, startSec: B(3), durationSec: W,  gain: 0.09, wave: 'triangle' },
    { freq: A3, startSec: B(4), durationSec: W,  gain: 0.09, wave: 'triangle' },
    { freq: F3, startSec: B(5), durationSec: W,  gain: 0.09, wave: 'triangle' },
    { freq: F3, startSec: B(6), durationSec: W,  gain: 0.09, wave: 'triangle' },
    { freq: G3, startSec: B(7), durationSec: W,  gain: 0.09, wave: 'triangle' },
    { freq: G3, startSec: B(8), durationSec: W,  gain: 0.09, wave: 'triangle' },
    { freq: C3, startSec: B(9), durationSec: W,  gain: 0.09, wave: 'triangle' },
  ];
}

// 60 s = 25 bars at 100 bpm (2.40 s/bar)
// Phrase A: bars 1-8  (offset 0)
// Phrase B: bars 9-16 (offset 19.2)
// Coda:     bars 17-25 (offset 38.4)
export const ACADEMY_LOOP_NOTES: Note[] = [
  ...phrase(0),
  ...phraseVariant(19.20),
  ...coda(38.40),
];

export const ACADEMY_LOOP_DURATION_SEC = 60;
